// src/pages/AdminUserManagement.jsx
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import useAdminStore from '../stores/useAdminStore';
import exportToPDF from '../utils/exportToPDF';
import useAuthStore from '../stores/useAuthStore';
import { useToast } from '../components/ToastContainer';
import axiosClient from '../services/api';

const AdminUserManagement = () => {
  const { user: currentUser } = useAuthStore();
  const toast = useToast();
  const { users, fetchUsers, isLoading, error } = useAdminStore();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isBanModalOpen, setIsBanModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'instructor',
    password: '',
    commissionRate: 20
  });

  useEffect(() => {
    fetchUsers({ startDate: dateRange.startDate, endDate: dateRange.endDate });
  }, [dateRange]);

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: new Date(value) }));
  };

  const resetDateRange = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateRange({ startDate: thirtyDaysAgo, endDate: now });
  };

  const exportUsers = () => {
    if (!users?.length) return;
    const columns = ['Name', 'Email', 'Role', 'Commission', 'Created At'];
    const rows = users.map(user => [
      user.name || 'N/A',
      user.email || 'N/A',
      user.role || 'N/A',
      user.role === 'instructor'
        ? `${user.commissionRate}%`
        : 'N/A',
      user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'
    ]);
    exportToPDF(users, 'User_Management_Report', columns, rows);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user._id));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newUser };
      if (newUser.role !== 'instructor') delete payload.commissionRate;

      await axiosClient.post('/users/admin/new/users', payload);
      toast.success('User created successfully!');
      setIsAddUserModalOpen(false);
      setNewUser({ name: '', email: '', role: 'instructor', password: '', commissionRate: 20 });
      fetchUsers({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create user');
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      const payload = { ...editingUser };
      if (payload.role !== 'instructor') delete payload.commissionRate;

      await axiosClient.put(`/users/admin/users/${editingUser._id}`, payload);
      toast.success('User updated successfully!');
      setIsEditUserModalOpen(false);
      fetchUsers({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update user');
    }
  };

  const openBanModal = (user) => {
    setEditingUser(user);
    setBanReason(user.banReason || '');
    setIsBanModalOpen(true);
  };

  const handleBanUser = async () => {
    try {
      await axiosClient.put(`/users/admin/users/${editingUser._id}/ban`, {
        banReason,
        isBanned: true
      });
      toast.success('User banned successfully!');
      setIsBanModalOpen(false);
      fetchUsers({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axiosClient.put(`/users/admin/users/${userId}/ban`, {
        isBanned: false,
        banReason: ''
      });
      toast.success('User unbanned successfully!');
      fetchUsers({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unban user');
    }
  };

  const handleBulkAction = async (action) => {
    if (!selectedUsers.length) return;

    const confirmMessage =
      action === 'delete' ? 'Delete selected users permanently?' :
        action === 'ban' ? 'Ban selected users?' :
          action === 'unban' ? 'Unban selected users?' : '';

    if (!window.confirm(`Are you sure you want to ${confirmMessage}`)) return;

    try {
      await axiosClient.post('/users/admin/users/bulk', {
        action,
        userIds: selectedUsers
      });
      toast.success(`Bulk ${action} completed successfully!`);
      setSelectedUsers([]);
      fetchUsers({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} users`);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout userRole={currentUser.role}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="admin">
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">User Management</h1>
              <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                Manage users, commissions, and bans
              </p>
            </div>
            <div className="flex space-x-3 mt-4 sm:mt-0">
              <button
                onClick={exportUsers}
                className="px-4 py-2 bg-success-500 hover:bg-success-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500"
              >
                Export PDF
              </button>
              <button
                onClick={() => setIsAddUserModalOpen(true)}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Add User
              </button>
            </div>
          </div>

          {/* Date Range Card */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Select Date Range</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate.toISOString().split('T')[0]}
                  onChange={handleDateRangeChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate.toISOString().split('T')[0]}
                  onChange={handleDateRangeChange}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={resetDateRange}
                  className="w-full px-4 py-2 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                >
                  Reset to 30 Days
                </button>
              </div>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedUsers.length > 0 && (
            <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex flex-wrap gap-3 items-center">
              <span className="text-primary-700 dark:text-primary-300 font-medium">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex flex-wrap gap-2 ml-auto">
                <button
                  onClick={() => handleBulkAction('ban')}
                  className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Ban Selected
                </button>
                <button
                  onClick={() => handleBulkAction('unban')}
                  className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  Unban Selected
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Delete Selected
                </button>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                <thead className="bg-neutral-50 dark:bg-neutral-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length > 0 && selectedUsers.length === users.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Role</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Commission</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                  {users.length > 0 ? (
                    <>
                      {users.map(user => (
                        <tr key={user._id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user._id)}
                              onChange={() => handleSelectUser(user._id)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-neutral-900 dark:text-white">{user.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-900 dark:text-white">{user.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 capitalize">
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${user.isBanned
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                              }`}>
                              {user.isBanned ? 'Banned' : 'Active'}
                            </span>
                            {user.isBanned && user.banReason && (
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1 max-w-xs truncate">
                                Reason: {user.banReason}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">
                            {user.role === 'instructor' ? `${user.commissionRate || 20}%` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                            <button
                              onClick={() => openEditModal(user)}
                              className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                              Edit
                            </button>
                            {user.isBanned ? (
                              <button
                                onClick={() => handleUnbanUser(user._id)}
                                className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                              >
                                Unban
                              </button>
                            ) : (
                              <button
                                onClick={() => openBanModal(user)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Ban
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </>) : (
                    <tr>
                      <td colSpan={7} className="py-12 text-center">
                        <div className="inline-block bg-white dark:bg-neutral-800 dark:border-neutral-700 p-8">
                          <div className="text-6xl mb-4">📚</div>
                          <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                            No user found
                          </h3>
                          <p className="text-neutral-600 dark:text-neutral-400">
                            Try adjusting your filters or check back later.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}

                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add User Modal */}
        {isAddUserModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-neutral-800 dark:text-neutral-300 rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Add New User</h3>
              </div>
              <form onSubmit={handleAddUser} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="instructor">Instructor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {newUser.role === 'instructor' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Commission Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={newUser.commissionRate}
                      onChange={(e) => setNewUser({ ...newUser, commissionRate: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                )}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="setPassword"
                    checked={!!newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.checked ? '' : undefined })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 dark:border-neutral-600 rounded"
                  />
                  <label htmlFor="setPassword" className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">
                    Set custom password
                  </label>
                </div>
                {newUser.password !== undefined && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Password</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsAddUserModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {isEditUserModalOpen && editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-neutral-800 dark:text-neutral-300 rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Edit User</h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Role</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {editingUser.role === 'instructor' && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Commission Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={editingUser.commissionRate || 20}
                      onChange={(e) => setEditingUser({ ...editingUser, commissionRate: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsEditUserModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveUser}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ban User Modal */}
        {isBanModalOpen && editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-neutral-800 dark:text-neutral-300 rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Ban User</h3>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-neutral-600 dark:text-neutral-400">
                  Provide a reason for banning <strong>{editingUser.name}</strong>:
                </p>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Enter ban reason..."
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 h-24"
                  required
                />
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsBanModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleBanUser}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Ban User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminUserManagement;